import type { Ref, InjectionKey, VueElementConstructor, PropType } from "vue";
import { DateTime } from "luxon";
import {
  EABFilterType,
  type DatePickerType,
  type FilterOption,
  type IEventOption,
  type SQDCEventActionTypeFieldType,
  type SQDCEventTypeFieldRelation,
  type ActionListActionFieldsFragment,
  type EventListEventFieldsFragment,
} from "@/graphql";
import type { EnumValueRelation } from "@/modules/sqdc/helper/SQDCEnumValueHelper";
import type { SerializedFilter } from "./FilterSerializer";

export type UserId = number;

export enum FilterEntity {
  EVENT,
  ACTION,
}

export type FilterEvent = {
  type: FilterEntity;
  index: number;
};

export enum AddFilterButtonPosition {
  left = "left",
  right = "right",
}

export class Filter {
  isNegated?: boolean;

  static UI: VueElementConstructor<any>;

  static Badge: VueElementConstructor<any>;

  serialize?(): SerializedFilter["values"];

  static deserialize?(values: SerializedFilter["values"]): Filter;
}

export type FilterConstructor = (abstract new (...args: any[]) => Filter) & {
  UI: VueElementConstructor<any>;
  Badge: VueElementConstructor<any>;
};

export interface EventFilter extends Filter {
  getEventFilter: () => FilterOption | undefined;
  isEventActive?(event: EventListEventFieldsFragment, boardId?: number): boolean;
}

export interface ActionFilter extends Filter {
  getActionFilter: () => FilterOption | undefined;
  isActionActive?(action: ActionListActionFieldsFragment): boolean;
}

export type EventFilters = {
  updatedAt: DateTime;
  eventFilters: EventFilter[];
  type?: EABFilterType;
};

export type ActionFilters = {
  updatedAt: DateTime;
  actionFilters: ActionFilter[];
  type?: EABFilterType;
};

export const defaultFilterType = EABFilterType.AND;

export const EventFilterInjectionKey = Symbol("EventFilterInjectionKey") as InjectionKey<
  Ref<EventFilters>
>;
export const ActionFilterInjectionKey = Symbol("ActionFilterInjectionKey") as InjectionKey<
  Ref<ActionFilters>
>;
export const ShowArchiveInjectionKey = Symbol("ShowArchiveInjectionKey") as InjectionKey<
  Ref<boolean>
>;

export const JumpToEventInjectionKey = Symbol("JumpToEventInjectionKey") as InjectionKey<
  (eventToJumpTo: IEventOption) => void
>;

export type BoardFilterUserOption = {
  id: number;
  name: string;
  displayName: string;
};

export type BoardFilterFlagOption = {
  id: number;
  name: string;
  color?: string;
  count: number;
  type?: string;
  systemFlag?: boolean;
  abbreviationId: number;
  displayNameId: number;
};

export type BoardFilterIdOption = {
  value: number;
  label: number;
  typeId: number;
};

export type BoardFilterOptions = {
  eventUsers: BoardFilterUserOption[];
  actionUsers: BoardFilterUserOption[];
  eventIds: BoardFilterIdOption[];
  actionIds: BoardFilterIdOption[];
  flags: BoardFilterFlagOption[];
};

export const BoardFilterOptionsInjectionKey = Symbol(
  "BoardFilterOptionsInjectionKey",
) as InjectionKey<{
  options: Ref<BoardFilterOptions>;
  loading: Ref<boolean>;
  queryKey: Ref<string>;
  load: () => Promise<void>;
}>;

export type FilterConfig = {
  id: number;
  text: string;
  mode: FilterEntity;
  filter: FilterConstructor;
  category?: "base" | "date" | "properties" | "custom";
  collapsed?: boolean;
};

export const FilterUIProps = {
  mode: {
    required: true,
    type: Number as PropType<FilterEntity>,
  },
  types: {
    type: Array as PropType<FilterEventActionTypes[]>,
    required: false,
    default: () => [],
  },
  siteId: {
    type: Number,
    required: false,
    default: undefined,
  },
};

export type FilterEventActionTypes = {
  id: number;
  uniqueName: string;
  nameId: number;
  fields: {
    id: number;
    nameId: number;
    type: SQDCEventActionTypeFieldType;
    visibleForAdminOnly?: string;
    relation: SQDCEventTypeFieldRelation;
    relationHasCategory: boolean;
    isArray: boolean;
    EnumValues: EnumValueRelation[];
    dateGranularity: DatePickerType;
  }[];
};

export const FilterBadgeProps = {
  types: {
    type: Array as PropType<FilterEventActionTypes[]>,
    required: false,
    default: () => [],
  },
};
